import { useState } from 'react';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { useAvailableRolesQuery } from '../hooks/useAvailableRolesQuery';
import { roleLabel } from '../utils/roleLabel';
import { MultiSelect } from '@/shared/components';
import type { MultiSelectOption } from '@/shared/components';

interface Props {
  onSelectionChange: (roleIds: string[]) => void;
}

/** Optional role multi-select on the Add User form, visible only to admins who can assign roles. */
export function UserRoleField({ onSelectionChange }: Props) {
  return (
    <PermissionGuard permission={{ resource: 'user', action: 'assign_role' }}>
      <UserRoleFieldBody onSelectionChange={onSelectionChange} />
    </PermissionGuard>
  );
}

function UserRoleFieldBody({ onSelectionChange }: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { hasPermission } = usePermissions();
  const canAssignElevated = hasPermission({ resource: 'user', action: 'assign_admin_role' });

  const query = useAvailableRolesQuery();
  const roles = query.data ?? [];

  // Missing flag defaults to elevated — under-gating an elevated role is a
  // security regression, over-gating a normal one is only a UX nuisance.
  const options: MultiSelectOption[] = roles.map((role) => {
    const elevated = role.is_elevated ?? true;
    return {
      id: role.id,
      name: elevated ? `${roleLabel(role)} (Elevated)` : roleLabel(role),
      disabled: elevated && !canAssignElevated,
    };
  });

  const handleChange = (next: string[]) => {
    setSelectedIds(next);
    onSelectionChange(next);
  };

  return (
    <div>
      <MultiSelect
        label="Roles"
        options={options}
        value={selectedIds}
        onChange={handleChange}
        loading={query.isLoading}
        error={query.isError ? 'Failed to load available roles.' : undefined}
        selectAllLabel="Select All"
      />
    </div>
  );
}
